import { useMutation } from "@apollo/client"
import {
  Button,
  Menu,
  MenuDivider,
  MenuItem,
  Position,
} from "@blueprintjs/core"
import CustomButton from "components/Atoms/CustomButton"
import CustomDialog from "components/Atoms/CustomDialog"
import CustomPopover from "components/Atoms/CustomPopover"
import Authorization from "components/Molecules/Authorization"
import VehicleDetailsDialog from "components/Templates/VehicleDetailsDialog"
import { VehicleMasterListQuery } from "generated/graphql"
import { BULK_UPDATE_VEHICLE_VENDORS } from "graphql/vehicle/vehicle.mutations.gql"
import useAuth from "hooks/useAuth"
import React, { ReactText, useCallback, useState } from "react"
import { useNavigate } from "react-router-dom"
import AppToaster from "utils/AppToaster"

type Vehicle = VehicleMasterListQuery["vehicleList"][0]
interface VehicleActionsProps {
  onDelete?: (vehicle?: Vehicle) => void
  onDetails?: (vehicle?: Vehicle) => void
  vehicle: Vehicle
  refetch?: () => void
}

const VehicleActions = ({
  vehicle,
  onDelete,
  refetch,
}: VehicleActionsProps) => {
  const [tab, setTab] = useState<ReactText | any>("details")
  const [isDetailOpen, setDetail] = useState(false)
  const [isOwnerOpen, setOwner] = useState({ isOpen: false, type: null })
  const navigate = useNavigate()
  const [verifyRejectOwner] = useMutation(BULK_UPDATE_VEHICLE_VENDORS)

  const { permissions } = useAuth()

  const vehicleId = vehicle?.id
  // Useful summary info to send along the route to establish a good context
  const vehicleSummary = {
    id: vehicleId,
    registrationNumber: vehicle?.registrationNumber,
    vehicleType: vehicle?.vehicleType,
  }
  function handleNewDriverAndOwner(type: "owner" | "driver") {
    // Send Summary Info Here
    navigate(`/d/registration/${type}/new`, { state: vehicleSummary })
  }

  const handleEdit = useCallback(() => {
    navigate(vehicleId)
  }, [vehicleId, navigate])

  const handleVerifyRejectOwner = (status: string) => {
    const s = status === "Verify Owner" ? "AV" : "NA"
    verifyRejectOwner({
      variables: {
        userIds: [vehicle?.owner?.id],
        status: s,
      },
    })
  }

  return (
    <>
      <CustomPopover
        position={Position.BOTTOM_LEFT}
        minimal={true}
        content={
          <Menu>
            <MenuDivider title="Add/Edit" />
            <MenuItem
              text="Swap Owner"
              icon="changes"
              onClick={(e: any) => handleNewDriverAndOwner("owner")}
            />
            <MenuItem
              text="Swap Driver"
              icon="changes"
              onClick={(e: any) => handleNewDriverAndOwner("driver")}
            />

            <MenuItem text="Vehicle" icon="edit" onClick={handleEdit} />
            <MenuDivider title="Actions" />
            <MenuItem
              icon="application"
              text="Details"
              onClick={(e: any) => setDetail(true)}
            />
            <Authorization required={["vt_add_update_vehicle"]}>
              {({ disabled }) => (
                <MenuItem
                  disabled={disabled}
                  icon="projects"
                  text="Archive Vehicle"
                  onClick={(e: any) => onDelete(vehicle)}
                />
              )}
            </Authorization>

            {vehicle?.owner &&
              permissions.includes("vt_core.vt_can_verify_transport_owner") && (
                <>
                  <MenuDivider title="Owner Verfication" />
                  {vehicle?.owner?.status === "AV" ? null : (
                    <MenuItem
                      icon="tick"
                      text="Verify Owner"
                      onClick={(e: any) =>
                        setOwner({ isOpen: true, type: "Verify Owner" })
                      }
                    />
                  )}
                  {vehicle?.owner?.status === "AV" && (
                    <MenuItem
                      icon="cross"
                      text="Reject Owner"
                      onClick={(e: any) =>
                        setOwner({ isOpen: true, type: "Reject Owner" })
                      }
                    />
                  )}
                </>
              )}
          </Menu>
        }
      >
        <CustomButton
          style={{ transform: "rotate(90deg)" }}
          icon="more"
          minimal={true}
        />
      </CustomPopover>
      <VehicleDetailsDialog
        selectedTab={tab}
        onTabChange={tab => {
          setTab(tab)
        }}
        vehicle={vehicle}
        isOpen={isDetailOpen}
        onClose={() => {
          setDetail(false)
        }}
      />

      <CustomDialog
        title="Verify Owner"
        isOpen={isOwnerOpen.isOpen}
        onClose={() => setOwner({ isOpen: false, type: null })}
        children={
          <div>
            <p>
              Are you sure you want to {isOwnerOpen.type}{" "}
              <span className="font-bold">{vehicle?.owner?.fullName}</span>?
            </p>
          </div>
        }
        footerRightButton={
          <Button
            onClick={async () => {
              await handleVerifyRejectOwner(isOwnerOpen.type)
              AppToaster.show({
                message: `${isOwnerOpen.type} ${vehicle?.owner?.fullName} successfully`,
                intent: "success",
                icon: "tick",
              })
              refetch && refetch()
              setOwner({ isOpen: false, type: null })
            }}
            className="rounded shadow-md"
            icon="tick"
          >
            {isOwnerOpen.type}
          </Button>
        }
      ></CustomDialog>
    </>
  )
}

export default VehicleActions
